import React, { useState } from 'react';
import {
  ShieldCheck, Database, ChevronDown, ChevronUp, Eye, ArrowLeft,
} from 'lucide-react';
import StepIndicator from '../onboarding/StepIndicator';
import ConnectorGrid from '../onboarding/ConnectorGrid';
import ConnectionForm from '../onboarding/ConnectionForm';
import ProjectSelector from './ProjectSelector';
import RunValidation from './RunValidation';
import ResultsPage from '../onboarding/ResultsPage';
import SDQLanding from './SDQLanding';
import type { OnboardingState, ConnectorType, CreateAndRunResult } from '../../types';

/* ─── SDQ Exception Framework Data ─── */
const SDQ_DIMENSIONS = [
  { dimension: 'Completeness', question: 'Are required fields populated?', rule: 'NOT NULL(policy_number)', exception: '12% records missing policy_number', risk: 'Medium' },
  { dimension: 'Accuracy', question: 'Are values correctly formatted and valid?', rule: "REGEX_MATCH(ssn, '^\\d{3}-\\d{2}-\\d{4}$')", exception: 'SSN = 123-4A-6789', risk: 'High' },
  { dimension: 'Consistency', question: 'Do fields logically agree?', rule: 'loss_date <= report_date', exception: 'report_date before loss_date', risk: 'High' },
  { dimension: 'Validity', question: 'Are values within acceptable ranges?', rule: 'age BETWEEN 0 AND 120', exception: 'age = -5', risk: 'Medium' },
  { dimension: 'Uniqueness', question: 'Are identifiers unique?', rule: 'COUNT(DISTINCT claim_id) = COUNT(*)', exception: 'Duplicate claim_id detected', risk: 'High' },
  { dimension: 'Timeliness', question: 'Is data fresh within SLA?', rule: 'CURRENT_DATE - last_update <= 1 day', exception: 'Data 45 days old', risk: 'Medium' },
  { dimension: 'Referential Integrity', question: 'Do foreign keys resolve?', rule: 'claim.policy_id EXISTS IN policy.id', exception: 'Orphan policy_id found', risk: 'High' },
  { dimension: 'Semantic Coherence', question: 'Are distributions statistically plausible?', rule: 'claim_amount < Q3 + 3*IQR', exception: 'Claim = $120,000 (historical max $40,000)', risk: 'High' },
];

const SCORE_BANDS = [
  { range: '90 - 100', label: 'High Confidence Data', color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30' },
  { range: '75 - 89', label: 'Moderate Risk', color: 'text-blue-500 bg-blue-500/10 border-blue-500/30' },
  { range: '60 - 74', label: 'Elevated Risk', color: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30' },
  { range: '< 60', label: 'Critical Data Integrity Failure', color: 'text-red-500 bg-red-500/10 border-red-500/30' },
];

function riskBadge(risk: string) {
  if (risk === 'High') return 'bg-red-500/15 text-red-400 border-red-500/30';
  if (risk === 'Medium') return 'bg-orange-500/15 text-orange-400 border-orange-500/30';
  return 'bg-blue-500/15 text-blue-400 border-blue-500/30';
}

function SDQFrameworkOverview({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <div className="bg-gradient-to-br from-emerald-500/8 via-teal-500/5 to-cyan-500/8 border border-emerald-400/30 rounded-xl overflow-hidden mb-6">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-emerald-500/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-cargo-text">Structured Data Quality Framework</h2>
            <p className="text-xs text-cargo-muted">Enforceable rules, example exceptions, and risk levels for enterprise data</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-cargo-muted">
          <Eye className="w-4 h-4" />
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </div>
      </button>

      {/* Expandable content */}
      {!collapsed && (
        <div className="px-6 pb-6 space-y-6">
          {/* Description */}
          <p className="text-sm text-cargo-text leading-relaxed">
            This structured data quality framework defines <strong>enforceable rules</strong>, example exceptions, and risk levels for evaluating
            structured enterprise data across critical quality dimensions.
          </p>

          {/* Dimension Table */}
          <div>
            <h3 className="text-sm font-bold text-cargo-text mb-3">Quality Dimensions</h3>
            <div className="overflow-x-auto rounded-lg border border-cargo-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-cargo-panel text-cargo-muted text-xs uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 font-medium">Dimension</th>
                    <th className="text-left px-4 py-2.5 font-medium">Business Question</th>
                    <th className="text-left px-4 py-2.5 font-medium">Sample Rule</th>
                    <th className="text-left px-4 py-2.5 font-medium">Example Exception</th>
                    <th className="text-left px-4 py-2.5 font-medium">Risk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cargo-border bg-cargo-surface">
                  {SDQ_DIMENSIONS.map((d) => (
                    <tr key={d.dimension} className="hover:bg-cargo-hover/30 transition-colors">
                      <td className="px-4 py-2.5 font-medium text-cargo-text whitespace-nowrap">{d.dimension}</td>
                      <td className="px-4 py-2.5 text-cargo-muted">{d.question}</td>
                      <td className="px-4 py-2.5">
                        <code className="text-xs bg-cargo-panel px-1.5 py-0.5 rounded text-cargo-text font-mono">{d.rule}</code>
                      </td>
                      <td className="px-4 py-2.5 text-cargo-muted text-xs">{d.exception}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium border ${riskBadge(d.risk)}`}>
                          {d.risk}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Composite Scoring Model */}
          <div>
            <h3 className="text-sm font-bold text-cargo-text mb-3">SDQ Composite Scoring Model</h3>
            <div className="bg-cargo-surface border border-cargo-border rounded-lg p-5">
              <p className="text-sm text-cargo-muted mb-4">
                <code className="bg-cargo-panel px-2 py-1 rounded text-cargo-text font-mono text-xs">
                  SDQ Score = &Sigma; (Dimension Score &times; Weight)
                </code>
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {SCORE_BANDS.map((b) => (
                  <div key={b.range} className={`rounded-lg border px-4 py-3 text-center ${b.color}`}>
                    <p className="text-lg font-bold">{b.range}</p>
                    <p className="text-xs mt-0.5 opacity-80">{b.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const initialState: OnboardingState = {
  step: 0,
  connectorType: null,
  connection: {},
  connectionTested: false,
  selectedSchema: null,
  selectedTables: [],
  columns: {},
  suggestedRules: [],
  domain: 'default',
  projectName: '',
  groupName: '',
  createAndRunResult: null,
  profileResult: null,
  dictionaryResult: null,
};

interface SDQState {
  selectedProjectId: number | null;
  selectedGroupId: number | null;
  selectedProjectName: string;
  selectedGroupName: string;
  selectedSchema: string;
  selectedTable: string;
}

const initialSDQState: SDQState = {
  selectedProjectId: null,
  selectedGroupId: null,
  selectedProjectName: '',
  selectedGroupName: '',
  selectedSchema: '',
  selectedTable: '',
};

const steps = ['Connector', 'Connection', 'Project', 'Run', 'Results'];

const SDQWizard: React.FC = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [state, setState] = useState<OnboardingState>(initialState);
  const [sdqState, setSDQState] = useState<SDQState>(initialSDQState);
  const [frameworkCollapsed, setFrameworkCollapsed] = useState(true);

  const setStep = (step: number) => setState((s) => ({ ...s, step }));

  const setConnectorType = (connectorType: ConnectorType) =>
    setState((s) => ({
      ...s,
      connectorType,
      connection: { connector_type: connectorType },
      step: 1,
    }));

  const setConnection = (connection: OnboardingState['connection'], tested: boolean) =>
    setState((s) => ({ ...s, connection, connectionTested: tested }));

  const handleProjectGroupSelect = (project: any, group: any) => {
    setSDQState((s) => ({
      ...s,
      selectedProjectId: project?.id ?? s.selectedProjectId,
      selectedProjectName: project?.project_name ?? s.selectedProjectName,
      selectedGroupId: group?.id ?? null,
      selectedGroupName: group?.group_name ?? '',
    }));
  };

  const handleRunComplete = (result: CreateAndRunResult) => {
    setState((s) => ({ ...s, createAndRunResult: result, step: 4 }));
  };

  const next = () => setStep(state.step + 1);
  const back = () => setStep(Math.max(0, state.step - 1));
  const reset = () => {
    setState(initialState);
    setSDQState(initialSDQState);
    setShowWizard(false);
  };

  if (!showWizard) {
    return (
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-cargo-text">Structured Data Quality Assessment (SDQ)</h1>
          <p className="text-cargo-muted mt-1">
            View past assessments or start a new structured data quality run
          </p>
        </div>
        <SDQLanding onNewRun={() => setShowWizard(true)} />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => { reset(); }}
          className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors"
          title="Back to history"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-cargo-text">Structured Data Quality Assessment (SDQ)</h1>
          <p className="text-cargo-muted mt-1">
            Connect to a source, attach a project, and run data quality validation
          </p>
        </div>
      </div>

      <SDQFrameworkOverview collapsed={frameworkCollapsed} onToggle={() => setFrameworkCollapsed(!frameworkCollapsed)} />

      <StepIndicator steps={steps} current={state.step} />

      <div className="mt-8">
        {state.step === 0 && (
          <ConnectorGrid onSelect={setConnectorType} selected={state.connectorType} />
        )}
        {state.step === 1 && (
          <ConnectionForm
            state={state}
            setConnection={setConnection}
            onNext={next}
            onBack={back}
          />
        )}
        {state.step === 2 && (
          <ProjectSelector
            connection={state.connection}
            selectedProjectId={sdqState.selectedProjectId}
            selectedGroupId={sdqState.selectedGroupId}
            selectedProjectName={sdqState.selectedProjectName}
            selectedGroupName={sdqState.selectedGroupName}
            schema={sdqState.selectedSchema}
            table={sdqState.selectedTable}
            onSchemaChange={(v) => setSDQState((s) => ({ ...s, selectedSchema: v }))}
            onTableChange={(v) => setSDQState((s) => ({ ...s, selectedTable: v }))}
            onSelect={handleProjectGroupSelect}
            onNext={next}
            onBack={back}
          />
        )}
        {state.step === 3 && (
          <RunValidation
            connection={state.connection}
            schema={sdqState.selectedSchema || 'public'}
            tables={sdqState.selectedTable ? [sdqState.selectedTable] : []}
            projectId={sdqState.selectedProjectId!}
            groupId={sdqState.selectedGroupId!}
            projectName={sdqState.selectedProjectName}
            groupName={sdqState.selectedGroupName}
            domain={state.domain}
            onComplete={handleRunComplete}
            onBack={back}
          />
        )}
        {state.step === 4 && (
          <ResultsPage state={state} onReset={reset} />
        )}
      </div>
    </div>
  );
};

export default SDQWizard;
